// Student-side completion flow for a parent-sent Check-In (Phase 6, ported
// to rewise_std+1 in Phase 8). Deliberately plain: answer the questions,
// submit, get a warm acknowledgement -- no score, no percentage, ever. The
// parent sees the qualitative result + time taken back in parent-app; the
// student never sees "your parent will grade this" framing.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getPendingCheckins, submitCheckinResult, type PendingCheckin } from "../services/checkinService";

export default function CheckInCompletionPage() {
  const { session, isLoading } = useAuth();
  const { checkinId } = useParams<{ checkinId: string }>();
  const navigate = useNavigate();

  const [checkin, setCheckin] = useState<PendingCheckin | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [startedAt] = useState(() => Date.now());

  useEffect(() => {
    if (!session?.student_id) return;
    getPendingCheckins(session.student_id).then((pending) => {
      setCheckin(pending.find((c) => String(c.id) === checkinId) ?? null);
      setLoading(false);
    });
  }, [session?.student_id, checkinId]);

  function pickOption(questionIndex: number, optionIndex: number) {
    setAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }));
  }

  async function handleSubmit() {
    if (!checkin || !session?.student_id) return;
    setSubmitting(true);
    try {
      const timeTakenSeconds = Math.round((Date.now() - startedAt) / 1000);
      const payload = Object.entries(answers).map(([qIdx, optIdx]) => ({
        question_index: Number(qIdx),
        option_chosen: optIdx,
      }));
      await submitCheckinResult({
        checkinId: checkin.id,
        studentId: session.student_id,
        answers: payload,
        timeTakenSeconds,
      });
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading || !session || loading) {
    return <div className="max-w-2xl mx-auto p-6 text-gray-400">Loading…</div>;
  }

  if (!checkin) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-gray-500">This one's already been taken care of.</p>
        <button onClick={() => navigate("/dashboard")} className="mt-4 text-coral-warm font-medium">
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-orange-50 text-ocean-deep p-6 rounded-xl text-center">
          <p className="text-lg font-bold mb-1">Nice work!</p>
          <p className="text-sm">Your parent will see how it went.</p>
        </div>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-4 bg-coral-warm text-white px-5 py-2.5 rounded-lg font-medium"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const allAnswered = checkin.questions_json.every((_, idx) => answers[idx] !== undefined);

  return (
    <div className="max-w-2xl mx-auto p-6">
      <p className="text-xs font-semibold text-coral-warm uppercase tracking-wide mb-1">A quick check-in from home</p>
      <h1 className="text-2xl font-bold text-ocean-deep mb-1">{checkin.concept_name}</h1>
      <p className="text-gray-500 mb-6">{checkin.subject}</p>

      <div className="space-y-6 mb-6">
        {checkin.questions_json.map((q, qIdx) => (
          <div key={qIdx}>
            <p className="font-medium text-gray-900 mb-3 whitespace-pre-wrap">
              {qIdx + 1}. {q.question}
            </p>
            <div className="grid gap-2">
              {q.options.map((opt, optIdx) => (
                <button
                  key={optIdx}
                  onClick={() => pickOption(qIdx, optIdx)}
                  className={`text-left p-3 rounded-lg border ${
                    answers[qIdx] === optIdx ? "border-coral-warm bg-orange-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!allAnswered || submitting}
        className="bg-coral-warm text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
      >
        Submit
      </button>
    </div>
  );
}
